import {connect} from 'react-redux';

import {
  MessageStatusesEnum,
  MessageVisualStatusesEnum,
} from '../../../libs/shared/core/src/models/api/messageModel';
import {ComposerPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {deleteMessageTask} from '../../services/tasks/deleteMessageTask';
import {AppState} from '../../store/appState';
import {getConversations, getMessages} from '../../store/data/dataSelectors';
import {
  makeGetIsMessageVisuallyShared,
  makeGetIsPanelDraftOwner,
} from '../../store/data/messages/messagesSelectors';
import {getUserId} from '../../store/data/teammates/teammatesSelectors';
import {forceComposerPopoutAction} from '../../store/panels/composer/composerPanelActions';
import {PopupComposer} from './popupComposer';

/*
 * Props.
 */

interface PopupComposerContainerProps {
  panelId: ComposerPanelId;
  messageUid: string;
}

/*
 * Selectors.
 */

function getMessageUid(_state: AppState, props: PopupComposerContainerProps) {
  return props.messageUid;
}

function getPanelId(_state: AppState, props: PopupComposerContainerProps) {
  return props.panelId;
}

function makeGetMessage() {
  return createSelector(
    [getUserId, getMessageUid, getMessages, makeGetIsPanelDraftOwner(getPanelId)],
    (userId, messageUid, messages, isPanelDraftOwner) => {
      if (!isPanelDraftOwner(messageUid)) {
        return undefined;
      }

      const message = messages[messageUid];
      if (
        !message ||
        message.visualStatus !== MessageVisualStatusesEnum.DRAFT ||
        message.status !== MessageStatusesEnum.DRAFT_COMPOSE
      ) {
        return undefined;
      }

      if (message.isShared && message.authorId !== userId) {
        return undefined;
      }

      return message;
    },
  );
}

function makeGetConversation(getMessage: ReturnType<typeof makeGetMessage>) {
  return createSelector(
    [getMessage, getConversations],
    (message, conversations) => (message && conversations[message.conversationId]) || null,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<PopupComposerContainerProps>()(() => {
  const getMessage = makeGetMessage();
  return {
    message: getMessage,
    isMessageShared: makeGetIsMessageVisuallyShared(getMessage),
    conversation: makeGetConversation(getMessage),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<PopupComposerContainerProps>()(({panelId, messageUid}) => ({
  deleteMessage: () => deleteMessageTask(messageUid),
  onForcePopout: () => forceComposerPopoutAction(panelId),
}));

export const PopupComposerContainer = connect(mapStateToProps, mapDispatchToProps)(PopupComposer);
